// 关键词多类型标注
// 支持多种关键词类型同时高亮，重叠部分可显示所有层级

/**
 * 关键词类型配置（可扩展，新增类型只需在此添加）
 * priority 越大优先级越高，决定重叠时的背景色和文字颜色
 */
const KEYWORD_TYPES = {
  point: {
    priority: 1,
    label: "重点词",
    underlineColor: "#2563eb",
    bg: "rgba(219, 234, 254, 0.78)",
    textColor: "#1d4ed8",
    badgeBg: "#eff6ff",
    badgeColor: "#2563eb",
  },
  special: {
    priority: 2,
    label: "特殊重点词",
    underlineColor: "#7c3aed",
    bg: "rgba(237, 233, 254, 0.78)",
    textColor: "#6d28d9",
    badgeBg: "#f5f3ff",
    badgeColor: "#7c3aed",
  },
  warn: {
    priority: 3,
    label: "敏感词",
    underlineColor: "#ca8a04",
    bg: "rgba(254, 249, 195, 0.82)",
    textColor: "#a16207",
    badgeBg: "#fefce8",
    badgeColor: "#b45309",
  },
  forbid: {
    priority: 4,
    label: "违禁词",
    underlineColor: "#dc2626",
    bg: "rgba(254, 226, 226, 0.78)",
    textColor: "#b91c1c",
    badgeBg: "#fef2f2",
    badgeColor: "#dc2626",
  },
};

/**
 * 对 HTML 内容按关键词分组做高亮，返回替换后的 HTML。
 * @param {string} originContent HTML 或纯文本
 * @param {Record<string, string[]>} keywordGroups 关键词分组
 * @returns {string} 高亮后的 HTML
 */
function highlightHtml(originContent, keywordGroups) {
  const div = document.createElement("div");
  div.innerHTML = originContent ?? "";

  const textNodes = getTextNodeList(div);
  const textList = getTextInfoList(textNodes);
  const fullText = textList.map(({ text }) => text).join("");

  if (!fullText) return div.innerHTML;

  const matches = findAllMatches(fullText, keywordGroups);
  if (matches.length === 0) return div.innerHTML;

  const typeMap = buildCharTypeMap(fullText.length, matches);
  replaceTextNodes(textNodes, textList, typeMap, fullText);

  return div.innerHTML;
}

/**
 * 在文本中查找所有关键词匹配，保留类型信息（不合并区间）
 * @param {string} content
 * @param {Record<string, string[]>} keywordGroups
 */
function findAllMatches(content, keywordGroups) {
  const matches = [];
  const lowerContent = content.toLowerCase();

  for (const [type, keywords] of Object.entries(keywordGroups || {})) {
    if (!keywords || !keywords.length) continue;
    if (!KEYWORD_TYPES[type]) continue;

    for (const keyword of keywords) {
      if (!keyword) continue;
      const lowerKeyword = keyword.toLowerCase();
      let index = 0;
      while ((index = lowerContent.indexOf(lowerKeyword, index)) !== -1) {
        matches.push({
          start: index,
          end: index + keyword.length,
          type,
          keyword,
        });
        index += 1;
      }
    }
  }
  return matches;
}

/**
 * 为每个字符位置构建它所属的关键词类型集合
 */
function buildCharTypeMap(contentLength, matches) {
  const typeMap = Array.from({ length: contentLength }, () => new Set());
  for (const match of matches) {
    for (let i = match.start; i < match.end; i++) {
      typeMap[i].add(match.type);
    }
  }
  return typeMap;
}

/**
 * 将相同类型集合的连续字符归为一段
 */
function buildSegments(text, typeMap) {
  const segments = [];
  if (typeMap.length === 0) return segments;

  let segStart = 0;
  let prevKey = typesKey(typeMap[0]);

  for (let i = 1; i <= typeMap.length; i++) {
    const curKey = i < typeMap.length ? typesKey(typeMap[i]) : null;
    if (curKey !== prevKey) {
      segments.push({
        start: segStart,
        end: i,
        types: [...typeMap[segStart]],
        text: text.substring(segStart, i),
      });
      segStart = i;
      prevKey = curKey;
    }
  }
  return segments;
}

function typesKey(typeSet) {
  return [...typeSet].sort().join(",");
}

/**
 * 根据关键词类型组合计算内联样式
 */
function computeInlineStyle(types) {
  const sorted = [...types].sort(
    (a, b) =>
      (KEYWORD_TYPES[a].priority || 0) - (KEYWORD_TYPES[b].priority || 0),
  );
  const highest = KEYWORD_TYPES[sorted[sorted.length - 1]];
  if (!highest) return "";

  const shadows = sorted
    .map((type, index) => {
      const config = KEYWORD_TYPES[type];
      if (!config) return "";
      const offset = -(3 + index * 4);
      return `inset 0 ${offset}px 0 0 ${config.underlineColor}`;
    })
    .filter(Boolean)
    .join(", ");

  const paddingBottom = Math.max(0, (sorted.length - 1) * 4);

  return (
    `background-color:${highest.bg};` +
    `color:${highest.textColor};` +
    `box-shadow:${shadows};` +
    (paddingBottom > 0 ? `padding-bottom:${paddingBottom}px;` : "")
  );
}

function escapeHtml(text) {
  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}

/**
 * 将分段结果生成含多类型标记的 HTML
 */
function generateHTML(segments) {
  return segments
    .map((seg) => {
      const escapedText = escapeHtml(seg.text);
      if (seg.types.length === 0) return escapedText;

      const classes = seg.types.map((t) => `kw-${t}`).join(" ");
      const style = computeInlineStyle(seg.types);
      const dataTypes = seg.types.join(",");

      return (
        `<span data-highlight="yes" data-kw-types="${dataTypes}" ` +
        `class="kw-highlight ${classes}" style="${style}">` +
        `${escapedText}</span>`
      );
    })
    .join("");
}

/**
 * 逐个文本节点替换为高亮 span（从后往前，避免索引偏移）
 */
function replaceTextNodes(textNodes, textList, typeMap, fullText) {
  for (let i = textNodes.length - 1; i >= 0; i--) {
    const { startIdx, endIdx } = textList[i];
    const nodeTypeMap = typeMap.slice(startIdx, endIdx);

    const hasHighlight = nodeTypeMap.some((s) => s.size > 0);
    if (!hasHighlight) continue;

    const nodeText = fullText.substring(startIdx, endIdx);
    const segments = buildSegments(nodeText, nodeTypeMap);
    const html = generateHTML(segments);

    const wrapper = document.createElement("span");
    wrapper.innerHTML = html;
    const parent = textNodes[i].parentNode;
    while (wrapper.firstChild) {
      parent.insertBefore(wrapper.firstChild, textNodes[i]);
    }
    parent.removeChild(textNodes[i]);
  }
}

function getTextNodeList(dom) {
  const nodeList = [...dom.childNodes];
  const textNodes = [];
  while (nodeList.length) {
    const node = nodeList.shift();
    if (node.nodeType === node.TEXT_NODE) {
      node.wholeText && textNodes.push(node);
    } else {
      nodeList.unshift(...node.childNodes);
    }
  }
  return textNodes;
}

function getTextInfoList(textNodes) {
  let length = 0;
  return textNodes.map((node) => {
    const startIdx = length;
    const endIdx = length + node.wholeText.length;
    length = endIdx;
    return {
      text: node.wholeText,
      startIdx,
      endIdx,
    };
  });
}

/** 与检测高亮逻辑一致：在纯文本中命中过的关键词，按类型去重 */
const KEYWORD_DISPLAY_ORDER = ["forbid", "warn", "special", "point"];

function getMatchedKeywordsByType(plainText, keywordGroups) {
  const matches = findAllMatches(plainText || "", keywordGroups);
  const sets = {};
  for (const type of KEYWORD_DISPLAY_ORDER) {
    sets[type] = new Set();
  }
  for (const m of matches) {
    if (sets[m.type]) sets[m.type].add(m.keyword);
  }
  return KEYWORD_DISPLAY_ORDER.reduce((acc, type) => {
    acc[type] = [...sets[type]];
    return acc;
  }, {});
}

export {
  highlightHtml,
  KEYWORD_TYPES,
  getMatchedKeywordsByType,
  KEYWORD_DISPLAY_ORDER,
};
